import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { firestore, storage, withActor } from '../../backend'
import dayjs from 'dayjs'
import { collection, doc, updateDoc, query, where, onSnapshot, writeBatch, getDocs, getDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage'

// Annotating a sealed engine invoice is restricted by the rules to a diff
// containing `comments` only (isInvoiceAnnotationOnly): no actor stamp there.
const isCommentsOnly = data => {
	const keys = Object.keys(data)
	return keys.length === 1 && keys[0] === 'comments'
}

export const saveFile = createAsyncThunk('files/saveFile', async ({ id, data, payment }) => {
	if (id) {
		return updateDoc(doc(firestore, 'files', id), isCommentsOnly(data) ? data : withActor(data))
	} else {
		const fileId = doc(collection(firestore, 'files')).id
		const batch = writeBatch(firestore)

		batch.set(doc(firestore, 'files', fileId), withActor({ ...data, creationDate: dayjs().unix() }, { create: true }), { merge: true })

		if (payment) {
			const paymentId = doc(collection(firestore, 'payments')).id
			batch.set(
				doc(firestore, 'payments', paymentId),
				withActor({ ...payment, creationDate: dayjs().unix(), file: fileId }, { create: true }),
				{ merge: true }
			)
		}

		return batch.commit()
	}
})

export const fetchFiles = createAsyncThunk('files/fetchFiles', async (venueId, { dispatch }) => {
	try {
		const querySnapshot = await getDocs(query(collection(firestore, 'files'), where('etablissement', '==', venueId), where('isGlobal', '==', true)))
		const files = querySnapshot.docs
			.map(doc => ({ ...doc.data(), id: doc.id }))
			.sort((a, b) => {
				if (a.name.toUpperCase() > b.name.toUpperCase()) return 1
				if (a.name.toUpperCase() < b.name.toUpperCase()) return -1
				return 0
			})
		dispatch(setFiles(files))
	} catch (error) {
		console.log(error)
	}
})

export const filesSlice = createSlice({
	name: 'files',
	initialState: {
		value: [],
		status: 'idle',
		filesOfBooking: [],
		error: null,
		errorCode: null,
	},
	reducers: {
		setFiles: (state, action) => {
			state.value = action.payload
		},
		setFileStatus: (state, action) => {
			state.status = action.payload
		},
		setFilesOfBooking: (state, action) => {
			state.filesOfBooking = action.payload
		},
	},
	extraReducers: {
		[saveFile.pending]: (state, action) => {
			state.status = 'loading'
			state.error = null
			state.errorCode = null
		},
		[saveFile.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[saveFile.rejected]: (state, action) => {
			state.status = 'failed'
			state.error = action.error.message
			// Le code Firebase (`permission-denied` sur un verrou fiscal) sert à
			// dire POURQUOI l'écriture a échoué — sans lui, tout finit en
			// « Oups ! » et l'utilisateur croit à une panne (BS-2228).
			state.errorCode = action.error.code
		},
	},
})

export const fetchFilesOfBooking = (bookingId, venueId) => dispatch => {
	return onSnapshot(
		query(collection(firestore, 'files'), where('reservation', '==', bookingId), where('etablissement', '==', venueId)),
		querySnapshot => {
			const files = querySnapshot.docs
				.map(doc => {
					const { dateDB, ...data } = doc.data()
					return { ...data, id: doc.id }
				})
				.filter(({ isDeleted }) => !isDeleted)
			dispatch(setFilesOfBooking(files))
		},
		error => {
			console.log(error)
		}
	)
}

export const fetchFilesOfGroup = (groupId, venueId) => dispatch => {
	return onSnapshot(
		query(collection(firestore, 'files'), where('groupId', '==', groupId), where('etablissement', '==', venueId)),
		querySnapshot => {
			const files = querySnapshot.docs
				.map(doc => {
					const { dateDB, ...data } = doc.data()
					return { ...data, id: doc.id }
				})
				.filter(({ isDeleted }) => !isDeleted)
			dispatch(setFilesOfBooking(files))
		},
		error => {
			console.log(error)
		}
	)
}

export const loadFileFromId = async fileId => {
	const docSnap = await getDoc(doc(firestore, 'files', fileId))
	return { ...docSnap.data(), id: docSnap.id }
}

// Only caller re-links invoices to a booking with `{ groupId, reservation }`.
// The rules restrict that write on sealed engine invoices to exactly those two
// keys (isInvoiceBookingLinkOnly), so no actor stamp here.
export const updateFiles = data => async (_, getState) => {
	const files = getState().files.filesOfBooking
	const batch = writeBatch(firestore)

	files.forEach(file => {
		batch.update(doc(firestore, 'files', file.id), data)
	})

	return batch.commit()
}

export const uploadFile = async (path, file) => {
	const storageRef = ref(storage, path)
	const snapshot = await uploadBytes(storageRef, file)
	const url = await getDownloadURL(snapshot.ref)
	return { url, name: snapshot.metadata.name }
}

export const getDownloadURLFromPath = async path => {
	const storageRef = ref(storage, path)
	const url = await getDownloadURL(storageRef)
	return url
}

export const uploadFileResumable = (path, file) => {
	const storageRef = ref(storage, path)
	const uploadTask = uploadBytesResumable(storageRef, file)
	return { uploadTask, getDownloadURL }
}

export const cancelFile = (fileId, payments = []) => {
	const batch = writeBatch(firestore)
	const canceled = withActor({ status: 'canceled' })
	batch.update(doc(firestore, 'files', fileId), canceled)

	// Les fiches négatives « payées » (remboursements, convention par le signe)
	// ne s'annulent jamais ici : la trace d'un argent réellement sorti survit à
	// l'annulation du document — et les rules gèlent celles du moteur (l'update
	// ferait échouer tout le batch).
	payments
		.filter(payment => !(payment.status === 'paid' && (Number(payment.amount) || 0) < 0))
		.forEach(payment => {
			batch.update(doc(firestore, 'payments', payment.id), canceled)
		})

	return batch.commit()
}

export const updateFile = (id, data) => {
	return updateDoc(doc(firestore, 'files', id), withActor(data))
}

export const getFiles = state => state.files.value
export const getFilesStatus = state => state.files.status
export const getFilesErrorCode = state => state.files.errorCode
export const getFilesOfBooking = state => state.files.filesOfBooking

export const { setFiles, setFileStatus, setFilesOfBooking } = filesSlice.actions

export default filesSlice.reducer
